import { EmailHistory } from '../../email/EmailHistory';
import { EmailSend } from '../../email/EmailSend';
import BlockRounded from '@mui/icons-material/BlockRounded';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import DoNotDisturbOnRounded from '@mui/icons-material/DoNotDisturbOnRounded';
import EditRounded from '@mui/icons-material/EditRounded';
import LocalShippingRounded from '@mui/icons-material/LocalShippingRounded';
import PrintRounded from '@mui/icons-material/PrintRounded';
import ReceiptLongRounded from '@mui/icons-material/ReceiptLongRounded';
import UndoRounded from '@mui/icons-material/UndoRounded';
import { Alert, Box, Button, Card, Tab, Tabs, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useTabParam } from '../../../lib/useTabParam';
import { api, unwrap } from '../../../api/client';
import { useAuth } from '../../../auth/AuthProvider';
import { ConfirmDialog } from '../../../components/ConfirmDialog';
import { DataTable } from '../../../components/DataTable';
import { Money, Qty } from '../../../components/Money';
import { StatusChip } from '../../../components/StatusChip';
import { businessDate, dateTime, money } from '../../../lib/format';
import { useNotify } from '../../../lib/notify';
import { useShop } from '../../../shop/ShopProvider';
import {
  DetailHeader,
  DetailSkeleton,
  ErrorState,
  InfoRow,
  LinesTable,
  ReasonDialog,
  SummaryCard,
  SummaryGrid,
  TotalsBlock,
} from '../components/DocParts';
import { afterSaleKeys, idem, salesKeys } from '../lib/api';
import { dec, sum, toFixed } from '../lib/decimal';
import { invoiceStatus, orderActions, plural, progress } from '../lib/documents';
import { orderDoc, shopHeader } from '../print/builders';
import { A4Document } from '../print/documents';
import { usePrint } from '../print/usePrint';
import { WhatsappHistory, WhatsappSend } from '../../whatsapp';
import { AttachmentsPanel } from '../../attachments';
import { OrderQtyDialog } from './OrderQtyDialog';
import { useShopLogo } from '../../../lib/images';
import { MoreMenu, type LinkedGroup, type MoreAction } from '../../../components/MoreMenu';
import { useLinkedPayments } from '../../../components/useLinkedPayments';

type Dialog = 'confirm' | 'deliver' | 'invoice' | 'close' | 'cancel' | 'draft' | null;

export function OrderDetailPage() {
  const { id = '' } = useParams();
  const { can } = useAuth();
  const { settings, format } = useShop();
  const logo = useShopLogo();
  const notify = useNotify();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { print, element } = usePrint();
  const [dialog, setDialog] = useState<Dialog>(null);
  const [tab, setTab] = useTabParam(['lines', 'deliveries', 'invoices'] as const);

  const key = [...salesKeys.orders, id];
  const query = useQuery({
    queryKey: key,
    queryFn: () => unwrap(api.GET('/sales-orders/{id}', { params: { path: { id } } })),
  });
  const deliveries = useQuery({
    queryKey: [...salesKeys.deliveries, 'order', id],
    queryFn: () =>
      unwrap(api.GET('/deliveries', { params: { query: { salesOrderId: id, pageSize: 100 } } })),
  });
  const invoices = useQuery({
    queryKey: [...salesKeys.invoices, 'order', id],
    queryFn: () =>
      unwrap(
        api.GET('/sales-invoices', { params: { query: { salesOrderId: id, pageSize: 100 } } }),
      ),
  });
  const payments = useLinkedPayments({ salesOrderId: id });

  const refreshAll = async () => {
    await Promise.all(afterSaleKeys.map((k) => qc.invalidateQueries({ queryKey: [...k] })));
  };
  const version = query.data?.version ?? 1;

  const transition = useMutation({
    mutationFn: async (input: {
      kind: 'confirm' | 'close' | 'cancel' | 'draft';
      reason?: string;
    }) => {
      const path = { path: { id }, header: idem() };
      if (input.kind === 'confirm')
        return unwrap(api.POST('/sales-orders/{id}/confirm', { params: path, body: { version } }));
      if (input.kind === 'draft')
        return unwrap(api.POST('/sales-orders/{id}/draft', { params: path, body: { version } }));
      if (input.kind === 'close')
        return unwrap(
          api.POST('/sales-orders/{id}/close', {
            params: path,
            body: { version, ...(input.reason ? { reason: input.reason } : {}) },
          }),
        );
      return unwrap(
        api.POST('/sales-orders/{id}/cancel', {
          params: path,
          body: { version, reason: input.reason ?? '' },
        }),
      );
    },
    onSuccess: async (order, input) => {
      qc.setQueryData(key, order);
      const verbs = {
        confirm: 'confirmed',
        close: 'closed',
        cancel: 'cancelled',
        draft: 'moved back to draft',
      };
      notify.success(`Order ${order.number} ${verbs[input.kind]}`);
      await refreshAll();
    },
    onError: (e) => notify.error(e),
  });

  if (query.isLoading) return <DetailSkeleton />;
  if (query.isError || !query.data)
    return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;

  const order = query.data;
  const actions = orderActions(order, can);
  const delivered = sum(order.lines.map((l) => dec(l.qtyDelivered)));
  const invoiced = sum(order.lines.map((l) => dec(l.qtyInvoiced)));
  const totalBase = toFixed(sum(order.lines.map((l) => dec(l.qtyBase))), 4);
  const deliveredPct = progress(toFixed(delivered, 4), totalBase);
  const invoicedPct = progress(toFixed(invoiced, 4), totalBase);
  // Still owed on this order's invoices (the sum of what each posted invoice has outstanding).
  const amountDue = sum(
    (invoices.data?.data ?? [])
      .filter((i) => i.status !== 'CANCELLED')
      .map((i) => dec(i.outstanding)),
  );
  const shop = shopHeader(settings, undefined, logo);
  const menuActions: MoreAction[] = [
    ...(actions.close
      ? [
          {
            key: 'close',
            label: 'Close short',
            icon: <DoNotDisturbOnRounded />,
            onClick: () => setDialog('close'),
            danger: true,
          },
        ]
      : []),
    ...(actions.cancel
      ? [
          {
            key: 'cancel',
            label: 'Cancel order',
            icon: <BlockRounded />,
            onClick: () => setDialog('cancel'),
            danger: true,
          },
        ]
      : []),
  ];
  const linked: LinkedGroup[] = [
    {
      key: 'deliveries',
      title: 'Deliveries',
      icon: <LocalShippingRounded fontSize="small" />,
      docs: (deliveries.data?.data ?? []).map((d) => ({
        id: d.id,
        label: d.number,
        hint: `${businessDate(d.docDate, format)}${d.status === 'REVERSED' ? ' · reversed' : ''}`,
        to: `/sales/deliveries/${d.id}`,
      })),
      allTo: '?tab=deliveries',
    },
    {
      key: 'invoices',
      title: 'Invoices',
      icon: <ReceiptLongRounded fontSize="small" />,
      docs: (invoices.data?.data ?? []).map((i) => ({
        id: i.id,
        label: i.number,
        hint: `${businessDate(i.docDate, format)} · ${money(i.total, format)}`,
        to: `/sales/invoices/${i.id}`,
      })),
      allTo: '?tab=invoices',
    },
    payments,
  ];

  return (
    <Box>
      <DetailHeader
        back={{ to: '/sales/orders', label: 'Orders' }}
        title={order.number}
        status={{ status: order.status }}
        subtitle={
          <>
            {order.contact.name} · {businessDate(order.docDate, format)}
            {order.expectedDate && ` · expected ${businessDate(order.expectedDate, format)}`}
          </>
        }
        actions={
          <>
            {actions.edit && (
              <Button
                variant="outlined"
                startIcon={<EditRounded />}
                onClick={() => void navigate(`/sales/orders/${order.id}/edit`)}
              >
                Edit
              </Button>
            )}
            {actions.reopen && (
              <Button
                variant="outlined"
                startIcon={<UndoRounded />}
                onClick={() => setDialog('draft')}
              >
                Move to draft
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={<PrintRounded />}
              onClick={() => print('a4', <A4Document shop={shop} doc={orderDoc(order, format)} />)}
            >
              Print
            </Button>
            <WhatsappSend
              target={{
                documentType: 'sales_order',
                documentId: order.id,
                documentRef: order.number,
                contact: order.contact,
                vars: {
                  number: order.number,
                  name: order.contact.name,
                  amount: money(order.total, format),
                  date: businessDate(order.docDate, format),
                },
                pdf: {
                  fileName: `${order.number}.pdf`,
                  paper: 'a4',
                  render: () => <A4Document shop={shop} doc={orderDoc(order, format)} />,
                },
              }}
            />
            <EmailSend
              target={{
                documentType: 'sales_order',
                documentId: order.id,
                documentRef: order.number,
                contact: order.contact,
                vars: {
                  number: order.number,
                  name: order.contact.name,
                  amount: money(order.total, format),
                  date: businessDate(order.docDate, format),
                },
                pdf: {
                  fileName: `${order.number}.pdf`,
                  paper: 'a4',
                  render: () => <A4Document shop={shop} doc={orderDoc(order, format)} />,
                },
              }}
            />
            {actions.invoice && (
              <Button
                variant={actions.deliver ? 'outlined' : 'contained'}
                startIcon={<ReceiptLongRounded />}
                onClick={() => setDialog('invoice')}
              >
                Invoice
              </Button>
            )}
            {actions.deliver && (
              <Button
                variant="contained"
                startIcon={<LocalShippingRounded />}
                onClick={() => setDialog('deliver')}
              >
                Deliver
              </Button>
            )}
            {actions.confirm && (
              <Button
                variant="contained"
                startIcon={<CheckCircleRounded />}
                onClick={() => setDialog('confirm')}
              >
                Confirm
              </Button>
            )}
            <MoreMenu
              linked={linked}
              onDuplicate={
                can('sales.create')
                  ? () => void navigate('/sales/orders/new', { state: { copy: order } })
                  : undefined
              }
              exportModel="sales_order"
              exportId={order.id}
              actions={menuActions}
            />
          </>
        }
      />

      {order.status === 'CANCELLED' && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Cancelled {order.cancelledAt ? dateTime(order.cancelledAt, format) : ''}
          {order.cancelReason ? ` — “${order.cancelReason}”` : ''}.
        </Alert>
      )}
      {order.status === 'CLOSED' && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Closed {order.closedAt ? dateTime(order.closedAt, format) : ''}
          {order.cancelReason ? ` — “${order.cancelReason}”` : ''}. Nothing more will be delivered;
          what was delivered can still be invoiced.
        </Alert>
      )}
      {order.status === 'DRAFT' && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Draft — stock is not reserved until the order is confirmed.
        </Alert>
      )}

      <SummaryGrid>
        <SummaryCard
          label="Order total"
          value={<Money value={order.total} />}
          hint={plural(order.lines.length, 'line')}
        />
        <SummaryCard
          label="Delivered"
          value={`${String(deliveredPct)}%`}
          tone={deliveredPct >= 100 ? 'success' : deliveredPct > 0 ? 'warning' : undefined}
          hint={plural(deliveries.data?.page.total ?? 0, 'delivery', 'deliveries')}
        />
        <SummaryCard
          label="Invoiced"
          value={`${String(invoicedPct)}%`}
          tone={invoicedPct >= 100 ? 'success' : invoicedPct > 0 ? 'warning' : undefined}
          hint={plural(invoices.data?.page.total ?? 0, 'invoice')}
        />
        <SummaryCard
          label="Amount due"
          value={<Money value={toFixed(amountDue, format.currencyDecimals)} />}
          tone={amountDue > 0n ? 'warning' : invoicedPct > 0 ? 'success' : undefined}
          hint={
            amountDue > 0n ? 'Owed on invoices' : invoicedPct > 0 ? 'Fully paid' : 'Not invoiced'
          }
        />
        <SummaryCard
          label="Deliver from"
          value={order.location.name}
          hint={order.reference ? `Ref. ${order.reference}` : undefined}
        />
      </SummaryGrid>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0,1fr)', lg: 'minmax(0,1fr) 300px' },
          gap: 3,
          alignItems: 'start',
        }}
      >
        <Card>
          <Tabs
            value={tab}
            onChange={(_, v: typeof tab) => setTab(v)}
            sx={{ px: 1.5, borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab value="lines" label="Items" />
            <Tab
              value="deliveries"
              label={`Deliveries (${String(deliveries.data?.page.total ?? 0)})`}
            />
            <Tab value="invoices" label={`Invoices (${String(invoices.data?.page.total ?? 0)})`} />
          </Tabs>
          {tab === 'lines' && (
            <>
              <LinesTable lines={order.lines} withProgress />
              <Box sx={{ borderTop: 1, borderColor: 'divider' }}>
                <TotalsBlock doc={order} />
              </Box>
            </>
          )}
          {tab === 'deliveries' && (
            <Box sx={{ '& > .MuiCard-root': { boxShadow: 'none', border: 0 } }}>
              <DataTable
                rows={deliveries.data?.data}
                loading={deliveries.isLoading}
                rowKey={(r) => r.id}
                onRowClick={(r) => void navigate(`/sales/deliveries/${r.id}`)}
                columns={[
                  { key: 'n', header: 'Delivery', render: (r) => <b>{r.number}</b> },
                  { key: 'd', header: 'Date', render: (r) => businessDate(r.docDate, format) },
                  {
                    key: 'l',
                    header: 'Lines',
                    align: 'right',
                    render: (r) => <Qty value={r.lineCount} />,
                    hideBelow: 'sm',
                  },
                  { key: 's', header: 'Status', render: (r) => <StatusChip status={r.status} /> },
                ]}
                empty={
                  <Typography color="text.secondary" sx={{ p: 4, textAlign: 'center' }}>
                    Nothing delivered yet.
                  </Typography>
                }
              />
            </Box>
          )}
          {tab === 'invoices' && (
            <Box sx={{ '& > .MuiCard-root': { boxShadow: 'none', border: 0 } }}>
              <DataTable
                rows={invoices.data?.data}
                loading={invoices.isLoading}
                rowKey={(r) => r.id}
                onRowClick={(r) => void navigate(`/sales/invoices/${r.id}`)}
                columns={[
                  { key: 'n', header: 'Invoice', render: (r) => <b>{r.number}</b> },
                  {
                    key: 'd',
                    header: 'Date',
                    render: (r) => businessDate(r.docDate, format),
                    hideBelow: 'sm',
                  },
                  {
                    key: 's',
                    header: 'Status',
                    render: (r) => {
                      const s = invoiceStatus(r);
                      return <StatusChip status={s.status} label={s.label} />;
                    },
                  },
                  {
                    key: 't',
                    header: 'Total',
                    align: 'right',
                    render: (r) => <Money value={r.total} />,
                  },
                ]}
                empty={
                  <Typography color="text.secondary" sx={{ p: 4, textAlign: 'center' }}>
                    Not invoiced yet.
                  </Typography>
                }
              />
            </Box>
          )}
        </Card>

        <Card sx={{ p: 2.5 }}>
          <Typography variant="overline" color="text.secondary">
            Customer
          </Typography>
          <Typography variant="h5" sx={{ mt: 0.5 }}>
            {order.contact.name}
          </Typography>
          {order.contact.phone && (
            <Typography color="text.secondary">{order.contact.phone}</Typography>
          )}
          <Box sx={{ mt: 2 }}>
            <InfoRow label="Created">{dateTime(order.createdAt, format)}</InfoRow>
            {order.confirmedAt && (
              <InfoRow label="Confirmed">{dateTime(order.confirmedAt, format)}</InfoRow>
            )}
            <InfoRow label="Prices">
              {order.pricesIncludeTax ? 'Include tax' : 'Before tax'}
            </InfoRow>
          </Box>
          {order.notes && (
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="caption" color="text.secondary">
                Notes
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                {order.notes}
              </Typography>
            </Box>
          )}
        </Card>
      </Box>

      <ConfirmDialog
        open={dialog === 'confirm'}
        title={`Confirm ${order.number}?`}
        body={`The items are reserved at ${order.location.name}, so they stop showing as available for other sales.`}
        confirmLabel="Confirm order"
        onClose={() => setDialog(null)}
        onConfirm={() => transition.mutateAsync({ kind: 'confirm' }).catch(() => undefined)}
      />
      <ConfirmDialog
        open={dialog === 'draft'}
        title={`Move ${order.number} back to draft?`}
        body="The order is un-confirmed and its reserved stock is released, so you can edit it. Confirm it again when you're done."
        confirmLabel="Move to draft"
        onClose={() => setDialog(null)}
        onConfirm={() => transition.mutateAsync({ kind: 'draft' }).catch(() => undefined)}
      />
      <ReasonDialog
        open={dialog === 'close'}
        title={`Close ${order.number} short?`}
        body="Nothing more will be delivered and the remaining reservations are released. What was delivered can still be invoiced."
        confirmLabel="Close order"
        reasonRequired={false}
        onClose={() => setDialog(null)}
        onConfirm={(reason) => transition.mutateAsync({ kind: 'close', reason })}
      />
      <ReasonDialog
        open={dialog === 'cancel'}
        title={`Cancel ${order.number}?`}
        body="The order is cancelled and its reservations are released. This cannot be undone."
        confirmLabel="Cancel order"
        onClose={() => setDialog(null)}
        onConfirm={(reason) => transition.mutateAsync({ kind: 'cancel', reason })}
      />
      {(dialog === 'deliver' || dialog === 'invoice') && (
        <OrderQtyDialog
          open
          order={order}
          mode={dialog}
          onClose={() => setDialog(null)}
          onSubmit={async (input) => {
            if (dialog === 'deliver') {
              const d = await unwrap(
                api.POST('/deliveries', {
                  params: { header: idem() },
                  body: {
                    id: crypto.randomUUID(),
                    salesOrderId: order.id,
                    docDate: input.docDate,
                    lines: input.lines,
                  },
                }),
              );
              notify.success(`Delivery ${d.number} posted`);
            } else {
              const inv = await unwrap(
                api.POST('/sales-invoices', {
                  params: { header: idem() },
                  body: {
                    id: crypto.randomUUID(),
                    salesOrderId: order.id,
                    docDate: input.docDate,
                    ...(input.dueDate ? { dueDate: input.dueDate } : {}),
                    lines: input.lines,
                    ...(input.payment ? { payment: input.payment } : {}),
                  },
                }),
              );
              notify.success(
                input.payment
                  ? `Invoice ${inv.number} posted and paid`
                  : `Invoice ${inv.number} posted`,
              );
            }
            await refreshAll();
          }}
        />
      )}
      <AttachmentsPanel recordType="SALES_ORDER" recordId={order.id} sx={{ mt: 3 }} />
      <WhatsappHistory documentType="sales_order" documentId={order.id} sx={{ mt: 3 }} />
      <EmailHistory documentType="sales_order" documentId={order.id} sx={{ mt: 3 }} />
      {element}
    </Box>
  );
}
